import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import DiscardChangesConfirmationModal from "../components/modals/DiscardChangesConfirmationModal";
import { LEAVE_PAGE_CONFIRM_MESSAGE, LEAVE_PAGE_CONFIRM_TEXT, LEAVE_PAGE_CONFIRM_TITLE } from "../utils/constants";

// Blocks in-app navigation while hasUnsavedChanges is true and renders the confirmation modal - drop
// {confirmationModal} into the page's JSX.
// onBypass runs when navigation proceeds without prompting; onConfirm runs after the user confirms,
// before the hook navigates - return false from it to skip the auto-navigate (e.g. already redirected).
export default function useNavigationGuard({
    hasUnsavedChanges,
    onBypass,
    onConfirm,
    title = LEAVE_PAGE_CONFIRM_TITLE,
    bodyText = LEAVE_PAGE_CONFIRM_MESSAGE,
    confirmText = LEAVE_PAGE_CONFIRM_TEXT,
} = {}) {
    const history = useHistory();
    const [showConfirm, setShowConfirm] = useState(false);
    const pendingRef = useRef(null);
    const unblockRef = useRef(null);
    const onBypassRef = useRef(onBypass);
    const onConfirmRef = useRef(onConfirm);
    onBypassRef.current = onBypass;
    onConfirmRef.current = onConfirm;

    useEffect(() => {
        const unblock = history.block((location, action) => {
            if (!hasUnsavedChanges) {
                if (onBypassRef.current) {
                    onBypassRef.current(location, action);
                }
                return true;
            }
            pendingRef.current = { location, action };
            setShowConfirm(true);
            return false;
        });
        unblockRef.current = unblock;
        return () => {
            unblock();
            unblockRef.current = null;
        };
    }, [history, hasUnsavedChanges]);

    const confirmLeave = async () => {
        setShowConfirm(false);
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        if (!pending) {
            return;
        }
        const handledNavigation = onConfirmRef.current
            ? (await onConfirmRef.current(pending.location, pending.action)) === false
            : false;
        if (!handledNavigation) {
            history.push(pending.location);
        }
    };

    const cancelLeave = () => {
        setShowConfirm(false);
        pendingRef.current = null;
    };

    const confirmationModal = (
        <DiscardChangesConfirmationModal
            show={showConfirm}
            setShow={(show) => { if (!show) cancelLeave(); }}
            handleSubmit={confirmLeave}
            title={title}
            bodyText={bodyText}
            confirmText={confirmText}
        />
    );

    return { showConfirm, confirmLeave, cancelLeave, confirmationModal };
}
